import React from 'react';
import { AlertCircle } from 'lucide-react';
import { SkeletonCard } from '../../components/Skeleton';
import StructuredDataCard, { StructuredDataEmptyState } from './StructuredDataCard';

// The contract detail 'Structured Data' tab: one card per applicable schema
// (alphabetical, run or not), then orphaned outputs whose schema was since
// deactivated or retargeted.
export default function StructuredDataTab({ sd, canWriteContracts, canRunSchemas = canWriteContracts, isAdmin }) {
  const { data, loading, error, run, editField, addRow, updateRow, deleteRow, verify, runningIds } = sd;

  if (loading) return <SkeletonCard />;
  if (error) {
    return (
      <div className="callout red">
        <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
        <div className="callout-body">Couldn't load structured data: {error}</div>
      </div>
    );
  }
  if (!data) return null;

  const outputBySchema = new Map((data.outputs || []).map((o) => [o.schemaId, o]));
  const applicable = data.schemas || [];
  const orphaned = (data.outputs || []).filter((o) => o.orphaned);

  if (applicable.length === 0 && orphaned.length === 0) {
    return <StructuredDataEmptyState isAdmin={isAdmin} />;
  }

  // Sections are collapsed by default so multiple schemas scan as a list
  // (doc-112); a lone section skips the extra click and opens itself.
  const defaultOpen = applicable.length + orphaned.length === 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {applicable.map((schema) => (
        <StructuredDataCard
          key={schema.id}
          schema={schema}
          output={outputBySchema.get(schema.id) || null}
          canWrite={canWriteContracts}
          canRun={canRunSchemas}
          running={runningIds.has(schema.id)}
          sourceInfo={data.sourceInfo || null}
          defaultOpen={defaultOpen}
          onRun={(opts) => run(schema.id, opts)}
          onEditField={(fieldKey, value) => editField(schema.id, fieldKey, value)}
          onRowAdd={(fieldKey, row) => addRow(schema.id, fieldKey, row)}
          onRowUpdate={(fieldKey, index, row) => updateRow(schema.id, fieldKey, index, row)}
          onRowDelete={(fieldKey, index) => deleteRow(schema.id, fieldKey, index)}
          onVerify={() => verify(schema.id)}
        />
      ))}
      {orphaned.map((output) => (
        <StructuredDataCard
          key={output.id}
          schema={null}
          output={output}
          canWrite={false}
          running={false}
          defaultOpen={defaultOpen}
          onRun={() => {}}
        />
      ))}
    </div>
  );
}
